import { useEffect, useRef, useState } from 'react';
import { type BannersList } from '@cx-web/api-clients/banners-api/types';
import { trackEvent } from '@cx-web/utils/dataLayer';

import styles from './BannersCarousel.module.css';
import { getWebPImageUrlWithWidth } from './getWebPImageUrlWithWidth';
import { events } from './types';

// Desktop infinite-loop carousel (drag + auto-advance + progress bar). Pure presentational: no device
// hook — the parent BannersCarousel picks desktop vs grid and passes data as plain props, so this works
// identically in RR7 atlas (useMedia decides) and atlas-astro (useDevice decides). See ./BannersCarousel.
function BannersCarouselDesktop({ banners, interval = 3000 }: { banners: BannersList; interval?: number }) {
  const containerRef = useRef<HTMLDivElement>(null);
  const [currentIndex, setCurrentIndex] = useState(1);
  const [transitionEnabled, setTransitionEnabled] = useState(true);
  const [bannerWidth, setBannerWidth] = useState(0);
  const [dragStartX, setDragStartX] = useState<number | null>(null);
  const [dragOffset, setDragOffset] = useState(0);

  useEffect(() => {
    if (containerRef.current) {
      const updateWidth = () => {
        const width = containerRef.current!.clientWidth;
        setBannerWidth(width);
      };
      updateWidth();
      window.addEventListener('resize', updateWidth);
      return () => window.removeEventListener('resize', updateWidth);
    }
  }, []);

  const duplicateBanners = [banners[banners.length - 1], ...banners, banners[0]];

  const handleTransitionEnd = () => {
    if (currentIndex === duplicateBanners.length - 1) {
      setTransitionEnabled(false);
      setCurrentIndex(1);
    }
    if (currentIndex === 0) {
      setTransitionEnabled(false);
      setCurrentIndex(banners.length);
    }
  };

  useEffect(() => {
    if (!transitionEnabled) {
      const timeout = setTimeout(() => setTransitionEnabled(true), 50);
      return () => clearTimeout(timeout);
    }
  }, [transitionEnabled]);

  const handleMouseDown = (e: React.MouseEvent) => setDragStartX(e.clientX);
  const handleMouseMove = (e: React.MouseEvent) => {
    if (dragStartX !== null) setDragOffset(e.clientX - dragStartX);
  };
  const handleMouseUp = () => {
    if (dragStartX !== null) {
      const threshold = bannerWidth * 0.2;
      if (dragOffset > threshold) setCurrentIndex(prev => prev - 1);
      else if (dragOffset < -threshold) setCurrentIndex(prev => prev + 1);
      setDragStartX(null);
      setDragOffset(0);
    }
  };
  const handleTouchStart = (e: React.TouchEvent) => setDragStartX(e.touches[0].clientX);
  const handleTouchMove = (e: React.TouchEvent) => {
    if (dragStartX !== null) setDragOffset(e.touches[0].clientX - dragStartX);
  };
  const handleTouchEnd = () => {
    if (dragStartX !== null) {
      const threshold = bannerWidth * 0.2;
      if (dragOffset > threshold) setCurrentIndex(prev => prev - 1);
      else if (dragOffset < -threshold) setCurrentIndex(prev => prev + 1);
      setDragStartX(null);
      setDragOffset(0);
    }
  };

  const effectiveTransition = dragStartX !== null ? 'none' : transitionEnabled ? 'transform 0.5s ease-in-out' : 'none';

  const bannerStyle = {
    display: 'flex',
    width: `${duplicateBanners.length * 100}%`,
    transform: `translateX(-${(currentIndex * 100) / duplicateBanners.length}%)`,
    transition: effectiveTransition,
  };

  let normalizedIndex = currentIndex - 1;
  if (normalizedIndex < 0) normalizedIndex = banners.length - 1;
  if (normalizedIndex >= banners.length) normalizedIndex = 0;

  const progressBarKey = currentIndex;

  return (
    <div
      className={styles.carouselContainer}
      data-testid="banners-carousel-desktop"
      onMouseDown={handleMouseDown}
      onMouseLeave={handleMouseUp}
      onMouseMove={handleMouseMove}
      onMouseUp={handleMouseUp}
      onTouchEnd={handleTouchEnd}
      onTouchMove={handleTouchMove}
      onTouchStart={handleTouchStart}
      ref={containerRef}
    >
      <ul data-testid="carousel-list" onTransitionEnd={handleTransitionEnd} style={bannerStyle}>
        {duplicateBanners.map((banner, idx) => (
          <li
            key={idx}
            style={{
              width: `${100 / duplicateBanners.length}%`,
              listStyle: 'none',
              flexShrink: 0,
              aspectRatio: '3.45 / 1',
            }}
          >
            <a
              href={banner.ctaUrl}
              onClick={() => {
                trackEvent(events.SPLASH_BANNER_CLICK, {
                  eventAction: events.SPLASH_BANNER_CLICK,
                  label: banner.style,
                  bannerName: banner.bannerName,
                  bannerType: banner.bannerType,
                  bookieCode: banner.bookieCode,
                  url: banner.ctaUrl,
                  // idx is the position in duplicateBanners ([clone_of_last, ...banners, clone_of_first]),
                  // so map it back to the logical banner index — clones would otherwise report a wrong
                  // (or out-of-range) position.
                  position: (idx - 1 + banners.length) % banners.length,
                });
              }}
              rel="noreferrer"
              target="_blank"
            >
              <div aria-label={banner.imageLarge.alt} className={styles.slide}>
                <img
                  alt={banner.imageLarge.alt}
                  className={styles.bannerImg}
                  draggable={false}
                  fetchPriority={idx === 1 ? 'high' : 'low'}
                  src={getWebPImageUrlWithWidth(banner.imageLarge.url, 1100)}
                />
              </div>
            </a>
          </li>
        ))}
      </ul>

      {/* Progress bar sliders */}
      <div className={styles.progressContainer}>
        {banners.map((_, idx) => (
          <div className={styles.progressBar} key={idx} onClick={() => setCurrentIndex(idx + 1)}>
            {normalizedIndex === idx && (
              <div
                className={styles.progressFill}
                key={progressBarKey}
                onAnimationEnd={() => setCurrentIndex(prev => prev + 1)}
                style={{
                  animationDuration: `${interval}ms`,
                  animationPlayState: dragStartX !== null ? 'paused' : 'running',
                }}
              />
            )}
          </div>
        ))}
        <div className={styles.slideIndicator} data-testid="slide-indicator">
          {normalizedIndex + 1} / {banners.length}
        </div>
      </div>
    </div>
  );
}

export default BannersCarouselDesktop;
